import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { Button, Flex, Select, Table, Tag, Tooltip, message, Spin } from 'antd'
import type { TableProps } from 'antd'
import { Link, useNavigate } from 'react-router-dom'
import {
  EditOutlined,
  DeleteOutlined,
  BarChartOutlined,
} from '@ant-design/icons'
import { SearchInput } from '@/components/SearchInput'

import Title from 'antd/es/typography/Title'
import type { Test } from '@/data'
import { getTests } from '@/api/lop/flow-experiments/getTests'
import { updateTest } from '@/api/lop/flow-experiments/updateTest'
import { deleteTest } from '@/api/lop/flow-experiments/deleteTest'
import type { FlowMetrics as FlowMetricsType } from '@/data'
import { getFunnelsURL } from '@/api/client'
import { AuthContext } from '@/contexts/AuthContext'
import { hasPermission } from '@/auth/access'
import { resolveBackendSort } from '@/utils/backendSort'
import { getErrorMessage } from '@/utils/getErrorMessage'

const metricColor = (val: string | number | null | undefined): string => {
  if (val == null) return '#8c8c8c'
  const n = typeof val === 'string' ? parseFloat(val) : val
  if (isNaN(n) || n === 0) return '#8c8c8c'
  return n > 0 ? '#379e03' : '#ff4d4f'
}

const fmt = (v: string | number | null | undefined) =>
  v != null ? `${v}%` : '-'

const FlowMetrics: React.FC<{
  metrics?: FlowMetricsType
  showGrowth?: boolean
  trafficPct?: number
}> = ({ metrics, showGrowth = true, trafficPct }) => (
  <span style={{ fontSize: 12, marginLeft: 6, color: '#8c8c8c' }}>
    ({trafficPct != null && <>Traffic: {trafficPct}%, </>}
    Visitors:{' '}
    {metrics?.visitor_count != null
      ? metrics.visitor_count.toLocaleString()
      : '-'}
    , Leads:{' '}
    {metrics?.lead_count != null ? metrics.lead_count.toLocaleString() : '-'},
    CVR: {fmt(metrics?.cvr)}, {/* CPA: {fmt(metrics?.cpa)}, */}LTC:{' '}
    {fmt(metrics?.ltc)}
    {showGrowth && (
      <>
        , Growth:{' '}
        <span style={{ color: metricColor(metrics?.growth) }}>
          {fmt(metrics?.growth)}
        </span>
      </>
    )}
    )
  </span>
)

export const TestsList: React.FC = () => {
  const navigate = useNavigate()
  const auth = useContext(AuthContext)!
  const [tests, setTests] = useState<Test[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    total: 0,
  })

  const [sortField, setSortField] = useState('-created_at')
  const [statusFilter, setStatusFilter] = useState<string>()
  const [search, setSearch] = useState('')
  const hasFetched = useRef(false)
  const canReadTests = hasPermission(auth.user, 'lop:ui:action:read:tests')
  const canCreateTests = hasPermission(auth.user, 'lop:ui:action:create:tests')
  const canUpdateTests = hasPermission(auth.user, 'lop:ui:action:update:tests')

  const columnSortKeys = {
    name: 'name',
    status: 'status',
    created_at: 'created_at',
  } as const

  const fetchTests = useCallback(
    async (q: {
      page: number
      pageSize: number
      sort: string
      status: string | undefined
      search: string
    }) => {
      try {
        setLoading(true)
        setError(null)
        const filters: { key: string; value: string }[] = []
        if (q.status) filters.push({ key: 'status', value: q.status })
        if (q.search) filters.push({ key: 'name', value: q.search })
        const { data, meta } = await getTests({
          page: q.page,
          per_page: q.pageSize,
          include: 'metrics',
          sort: q.sort,
          ...(filters.length ? { filters } : {}),
        })
        setTests(data)
        setPagination({
          current: meta.current_page,
          pageSize: meta.per_page,
          total: meta.total,
        })
      } catch (err) {
        setError('Failed to load tests')
        console.error(err)
      } finally {
        setLoading(false)
      }
    },
    [],
  )

  const handleSearch = useCallback(
    (value: string) => {
      setSearch(value)
      fetchTests({
        page: 1,
        pageSize: pagination.pageSize,
        sort: sortField,
        status: statusFilter,
        search: value,
      })
    },
    [fetchTests, pagination.pageSize, sortField, statusFilter],
  )

  useEffect(() => {
    if (hasFetched.current) return
    if (!canReadTests) return
    hasFetched.current = true
    fetchTests({
      page: 1,
      pageSize: pagination.pageSize,
      sort: sortField,
      status: statusFilter,
      search,
    })
  }, [
    canReadTests,
    fetchTests,
    pagination.pageSize,
    sortField,
    statusFilter,
    search,
  ])

  const handleArchive = async (id: string) => {
    try {
      await updateTest(id, { status: 'archived' })
      setTests((prev) =>
        prev.map((t) => (t.id === id ? { ...t, status: 'archived' } : t)),
      )
      message.success('Test archived')
    } catch (err) {
      message.error(getErrorMessage(err, 'Failed to archive test'))
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await deleteTest(id)
      setTests((prev) => prev.filter((t) => t.id !== id))
      message.success('Test deleted')
    } catch (err) {
      message.error(getErrorMessage(err, 'Failed to delete test'))
    }
  }

  const columns: TableProps<Test>['columns'] = [
    {
      title: 'Test Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      render: (text, record) =>
        canUpdateTests ? (
          <Link to={`/tests/${record.id}/edit`}>{text}</Link>
        ) : (
          text
        ),
    },
    {
      title: 'Lead Flows',
      key: 'lead_flows',
      render: (_, record) => {
        const variants = record.flows ?? []
        if (variants.length === 0 && !record.base_flow) return '-'
        const variantTraffic = variants.reduce(
          (sum, v) => sum + (v.traffic_percentage ?? 0),
          0,
        )
        const baseTraffic = 100 - variantTraffic
        return (
          <Flex vertical gap={4}>
            {record.base_flow && (
              <div key={`base-${record.base_flow.slug}`}>
                <a
                  href={`${getFunnelsURL()}/t/${record.base_flow.template_slug}/?flow=${record.base_flow.slug}&skiptesting=true&include=brand`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {record.base_flow.name}
                </a>
                <FlowMetrics
                  metrics={record.base_flow.metrics}
                  showGrowth={false}
                  trafficPct={baseTraffic}
                />
              </div>
            )}
            {variants.map((entry) =>
              entry.flow ? (
                <div key={entry.flow.slug}>
                  <a
                    href={`${getFunnelsURL()}/t/${entry.flow.template_slug}/?flow=${entry.flow.slug}&skiptesting=true&include=brand`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {entry.flow.name}
                  </a>
                  <FlowMetrics
                    metrics={entry.flow.metrics}
                    trafficPct={entry.traffic_percentage}
                  />
                </div>
              ) : null,
            )}
          </Flex>
        )
      },
    },
    {
      title: 'Visitors',
      key: 'lead_count',
      sorter: false,
      render: (_, record) => {
        const toNum = (v: unknown) => {
          const n = Number(v)
          return Number.isFinite(n) ? n : 0
        }
        const base = toNum(record.base_flow?.metrics?.visitor_count)
        const variants = (record.flows ?? []).reduce(
          (sum, v) => sum + toNum(v.flow?.metrics?.visitor_count),
          0,
        )
        const total = base + variants
        return total.toLocaleString()
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: true,
      filters: [
        { text: 'Active', value: 'active' },
        { text: 'Paused', value: 'paused' },
        { text: 'Archived', value: 'archived' },
      ],
      filterMultiple: false,
      render: (status: Test['status'], record) => {
        if (!canUpdateTests) {
          if (!status) return '—'
          const colorMap = {
            active: 'green',
            paused: 'orange',
            archived: 'default',
          }
          const labelMap = {
            active: 'Active',
            paused: 'Paused',
            archived: 'Archived',
          }
          return <Tag color={colorMap[status]}>{labelMap[status]}</Tag>
        }
        return (
          <Select
            value={status ?? 'paused'}
            size="small"
            variant="borderless"
            style={{ width: 110 }}
            onChange={async (value) => {
              try {
                await updateTest(record.id, { status: value })
                setTests((prev) =>
                  prev.map((t) =>
                    t.id === record.id ? { ...t, status: value } : t,
                  ),
                )
                message.success('Status updated')
              } catch (err) {
                message.error(getErrorMessage(err, 'Failed to update status'))
              }
            }}
            options={[
              { value: 'active', label: <Tag color="green">Active</Tag> },
              { value: 'paused', label: <Tag color="orange">Paused</Tag> },
              { value: 'archived', label: <Tag color="default">Archived</Tag> },
            ]}
          />
        )
      },
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      sorter: true,
      defaultSortOrder: 'descend',
      render: (value: string) =>
        value
          ? new Date(value).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })
          : '—',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Flex gap="middle">
          {canUpdateTests ? (
            <Tooltip title="Edit">
              <Link to={`/tests/${record.id}/edit`}>
                <EditOutlined style={{ fontSize: 16 }} />
              </Link>
            </Tooltip>
          ) : null}
          <Tooltip title="Reports">
            <BarChartOutlined style={{ fontSize: 16, cursor: 'pointer' }} />
          </Tooltip>
          {canUpdateTests ? (
            <Tooltip
              title={record.status === 'archived' ? 'Delete' : 'Archive'}
            >
              <DeleteOutlined
                style={{ fontSize: 16, color: '#ff4d4f', cursor: 'pointer' }}
                onClick={() =>
                  record.status === 'archived'
                    ? handleDelete(record.id)
                    : handleArchive(record.id)
                }
              />
            </Tooltip>
          ) : null}
        </Flex>
      ),
    },
  ]

  if (error) {
    return (
      <div>
        <Title level={4}>A/B Tests</Title>
        <p style={{ color: '#ff4d4f' }}>{error}</p>
        <Button
          onClick={() =>
            fetchTests({
              page: 1,
              pageSize: pagination.pageSize,
              sort: sortField,
              status: statusFilter,
              search,
            })
          }
        >
          Retry
        </Button>
      </div>
    )
  }

  return (
    <div>
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          A/B Tests
        </Title>
        <Flex gap="small" align="center">
          <SearchInput onSearch={handleSearch} />
          <Button
            onClick={() => {
              const next = statusFilter === 'archived' ? undefined : 'archived'
              setStatusFilter(next)
              fetchTests({
                page: 1,
                pageSize: pagination.pageSize,
                sort: sortField,
                status: next,
                search,
              })
            }}
          >
            {statusFilter === 'archived' ? 'View Active' : 'View Archived'}
          </Button>
          {canCreateTests ? (
            <Button type="primary" onClick={() => navigate('/tests/new')}>
              New Test
            </Button>
          ) : null}
        </Flex>
      </Flex>
      {canReadTests ? (
        <Spin spinning={loading}>
          <Table<Test>
            rowKey="id"
            columns={columns}
            dataSource={tests}
            pagination={pagination}
            onChange={(p, filters, sorter) => {
              const sort = resolveBackendSort(
                sorter,
                columnSortKeys,
                '-created_at',
              )
              const status = filters.status?.[0] as string | undefined
              setSortField(sort)
              setStatusFilter(status)
              fetchTests({
                page: p.current ?? 1,
                pageSize: p.pageSize ?? pagination.pageSize,
                sort,
                status,
                search,
              })
            }}
          />
        </Spin>
      ) : null}
    </div>
  )
}
